import React, { Fragment, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import mixpanel from 'mixpanel-browser';
import { format, parseISO, fromUnixTime, formatDistanceToNow } from 'date-fns';

// Firebase
import { db } from '../../config/firebase-config';
import { collection, getDocs, getDoc, addDoc, updateDoc, doc, setDoc, deleteDoc, query, where, onSnapshot, orderBy, startAfter, limit } from 'firebase/firestore';

// Redux
import { connect } from 'react-redux';

// Actions
import { setAlert } from '../../actions/alertActions';
import { pauseAd, activateAd, stopAd, resumeAd, editAdTitle } from '../../actions/adActions';
import { getPostById } from '../../actions/postActions';

// Modal
import Modal from '../../components/modal/Modal';
import ModalContainer from '../../components/modal/ModalContainer';

// Components - imported
import { Avatar, Button } from '@material-ui/core';
import DefaultAvatar from 'react-avatar';
import Spinner from '../common/Spinner';
import Post from '../Post/Post';

// Icons - material UI
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import HeadsetMicOutlinedIcon from '@material-ui/icons/HeadsetMicOutlined';
import AccountCircleIcon from '@material-ui/icons/AccountCircle';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import PhoneInTalkIcon from '@material-ui/icons/PhoneInTalk';
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined';
import LocalMallOutlinedIcon from '@material-ui/icons/LocalMallOutlined';
import MoneyOffIcon from '@material-ui/icons/MoneyOff';
import EditIcon from '@material-ui/icons/Edit';
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined';

const initialState = {
    title: '',
};

const Ad_Details = ({
    post: {
        post
    },
    ad_details,
    setAlert,
    pauseAd, 
    activateAd, 
    stopAd,
    resumeAd,
    editAdTitle,
    getPostById,
    history
}) => {

    // Analytics 
    const [sentMixpanel, setSentMixpanel] = useState(false);

    // Screen Width
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // Input field data
    const [formData, setFormData] = useState(initialState);

    // Get the current URL and extract the "redirect" parameter
    const url_filter = (window.location.href);
    const url = new URL(url_filter);
    const updated = url.searchParams.get("updated");

    // Listen for window resize event and update window width state
    useEffect(() => {

        // Add event listener for window resize
        window.addEventListener('resize', () => handleWindowSizeChange());

        // Clean up by removing event listener on component unmount
        return () => window.removeEventListener('resize', () => handleWindowSizeChange());
    }, []);

    // Update window width state on window resize
    const handleWindowSizeChange = () => {
        setWindowWidth(window.innerWidth);
    };

    // Determine mobile and tablet screen sizes
    const isMobile = windowWidth <= 769;
    const isTablet = windowWidth <= 1000;

    // -- Modal toggle values --
    
    // Stop Ad
    const [stopModal, setStopModal] = useState(false);
    // Pause Ad
    const [pauseModal, setPauseModal] = useState(false);
    // Resume Ad
    const [resumeModal, setResumeModal] = useState(false);

    // -- END: Modal toggle values --

    // -- Tooltip Modals

    // Overview stats info
    const [toolTipModal, setToolTipModal] = useState(false);
    const [toolTipType, setToolTipType] = useState("");

    // -- END: Tooltip Modals

    const [adDoc, setAdDoc] = useState(null);
    const [adLoading, setAdLoading] = useState(true);
    const [statusComponent, setStatusComponent] = useState(null);
    const [timeLeft, setTimeLeft] = useState(null);
    const [adBudget, setAdBudget] = useState(null);
    const [adSpend, setAdSpend] = useState(null);
    const [adDuration, setAdDuration] = useState(null);
    const [ctaType, setCtaType] = useState(null);
    
    const [editMode, setEditMode] = useState(false);
    
    useEffect(() => {
        if (!ad_details) return;
    
        const docRef = doc(db, 'ads', ad_details._id);
    
        // Subscribe to real-time updates
        const unsubscribe = onSnapshot(docRef, (snapshot) => {
            if (snapshot.exists()) {
                setAdDoc({ _id: snapshot.id, ...snapshot.data() });

                const amount_budgeted = (snapshot.data().budget.amount_budgeted / 100).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD'
                });

                const total_spent = (snapshot.data().budget.total_spent / 100).toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD'
                });

                setAdBudget(amount_budgeted);
                setAdSpend(total_spent);
            } else {
                setAdDoc(null);
                setAdBudget(null);
                setAdSpend(null);
            }
            setAdLoading(false);
        }, (error) => {
            setAdDoc(null);
            setAdBudget(null);
            setAdSpend(null);
            console.log("Error listening to Ad document:", error);
            setAdLoading(false);
        }
        );
    
        // Clean up listener when unmounting
        return () => unsubscribe();
    }, [ad_details]);

    useEffect(() => {
        if (adDoc) {
            renderStatusComponent();

            getPostById(adDoc.postId)
        } else {
            setStatusComponent(<span>--</span>);
        }
    }, [adDoc]);

    // Initialize Mixpanel and track settings account page view
    const handleMixpanel = () => {
        mixpanel.init(process.env.REACT_APP_MIXPANEL_ID);
        mixpanel.track("Ad Detail Page View");
    }

    // Check if the code is running in production and Mixpanel event hasn't been sent yet
    if(process.env.NODE_ENV === 'production' && !sentMixpanel) {

        // Initialize Mixpanel and track the event
        handleMixpanel();

        // Set the flag to indicate that Mixpanel event has been sent
        setSentMixpanel(true);
    }

    // Go back to main settings page
    const goBack = () => {
        window.location.href = '/ads';
    }

    const toggleToolTipModal = (type) => {

        // Toggles the resume modal
        setToolTipModal(!toolTipModal); 
        setToolTipType(type);
    }

    // Toggle the Pause Ad confirmation modal
    const togglePauseModal = () => {

        // Toggles the pause modal
        setPauseModal(!pauseModal); 
    }

    // Pause the Ad
    const handlePause = () => {
        if(ad_details) {
            let doc_id = ad_details._id

            pauseAd(ad_details);

            // Mixpanel event tracking for pausing an ad
            if (process.env.NODE_ENV === 'production') {
                mixpanel.track("Boost Post: Pause Ad", {
                    "Ad ID": `${doc_id}`
                });
            }
        }

        // Closes the pause modal
        setPauseModal(false);
    }

    // Toggle the Resume Ad confirmation modal
    const toggleResumeModal = () => {

        // Toggles the resume modal
        setResumeModal(!resumeModal); 
    }

    // Resume the Ad
    const handleResume = () => {
        if(ad_details) {
            let doc_id = ad_details._id

            resumeAd(ad_details);

            // Mixpanel event tracking for pausing an ad
            if (process.env.NODE_ENV === 'production') {
                mixpanel.track("Boost Post: Resume Ad", {
                    "Ad ID": `${doc_id}`
                });
            }
        }

        // Closes the resume modal
        setResumeModal(false);
    }

    // Toggle the Stop Ad confirmation modal
    const toggleStopModal = () => {

        // Toggles the stop modal
        setStopModal(!stopModal); 
    }

    // Stop the Ad
    const handleStop = () => {
        if(ad_details) {
            let doc_id = ad_details._id

            stopAd(ad_details);

            // Mixpanel event tracking for pausing an ad
            if (process.env.NODE_ENV === 'production') {
                mixpanel.track("Boost Post: Stopped Ad", {
                    "Ad ID": `${doc_id}`
                });
            }
        }

        // Closes the stop modal
        setStopModal(false);
    }

    const renderStatusComponent = () => {
        if(adDoc.status === 'active') {
            setStatusComponent(<span style={{color: '#155724', fontWeight: 500}}>Running</span>);
        } else if(adDoc.status === 'paused') {
            setStatusComponent(<span style={{color: '#383D41', fontWeight: 500}}>Paused</span>);
        } else if(adDoc.status === 'rejected') {
            setStatusComponent(<span style={{color: '#721C24', fontWeight: 500}}>Rejected</span>);
        } else if(adDoc.status === 'in review') {
            setStatusComponent(<span style={{color: '#856404', fontWeight: 500}}>In Review</span>);
        } else if(adDoc.status === 'stopped') {
            setStatusComponent(<span style={{color: '#721C24', fontWeight: 500}}>Stopped</span>);
        } else if(adDoc.status === 'checkout') {
            setStatusComponent(<span style={{color: '#333', fontWeight: 500}}>Not Paid</span>);
        } else if(adDoc.status === 'scheduled') {
            setStatusComponent(<span style={{color: '#0C5460', fontWeight: 500}}>Scheduled</span>);
        } else if(adDoc.status === 'completed') {
            setStatusComponent(<span style={{color: '#333', fontWeight: 500}}>Completed</span>);
        } else {
            setStatusComponent(<span style={{color: '#333', fontWeight: 500}}>{adDoc.status}</span>);
        }
    }

    useEffect(() => {
        // Load ad data
        if (adDoc) {

            // Create a copy of the initial form data
            const adData = { ...initialState };
            for (const key in adDoc) {
                if (key in adData) adData[key] = adDoc[key];
            }

            setFormData(adData);

            if(adDoc.title === null || adDoc.title === "") {
                setEditMode(true);
            }
        }

    }, [adDoc]);

    const { 
        title
    } = formData;

    const onChange = e => setFormData({ ...formData, [e.target.name]: e.target.value});

    const handleSaveChanges = () => {
        editAdTitle({adId: adDoc._id, title});
        setEditMode(false);
    }

    useEffect(() => {
        if(adDoc) {
            let time_left;

            if (adDoc.status === "paused" && adDoc.paused_at) {
                // If paused, freeze the countdown
                time_left = adDoc.end_time_adjusted - (adDoc.paused_at + adDoc.time_paused);
            } else {
                // If active, update countdown
                time_left = adDoc.end_time_adjusted - Date.now();
            }
            setTimeLeft(time_left);
            setAdDuration(Date.now() - adDoc.start_time - adDoc.time_paused);
            setCtaType(adDoc.cta.type);
        }

    }, [adDoc]);

    // Help function (format ms into date format)
    const formatTimeLeft = (ms) => {
        if (ms <= 0) return "Expired";

        const totalSeconds = Math.floor(ms / 1000);
        const days = Math.floor(totalSeconds / (3600 * 24));
        const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);

        if (days > 0) return `${days}d ${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    }

    return (
        <Fragment>
            <div className="orderPage">

                {/* OrderPage Header */}
                <div className="orderPage__header">
                    {isTablet && (

                        // Back Button & Badge Value
                        <div onClick={goBack} className="profile__btnContainer">
                            <ArrowBackIcon />
                        </div>
                    )}

                    {/* Title */}
                    <div className="orderPage__headerText">
                        <h3>
                            Analytics
                        </h3>
                    </div>
                </div>

                <div className="orderPage__body">
                    <div style={{padding: '0 20px'}}>
                        {/* Render the GettingStarted component */}
                        <p style={{ color: 'rgb(115, 115, 115)'}}>
                            This ad is currently: {statusComponent}
                        </p>

                        {!editMode ? (
                            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '24px'}}>
                                <h2>{adDoc?.title ? adDoc.title : 'Enter a title...' }</h2>
                                <div className='icon_18 hover_btn_bg' onClick={() => setEditMode(true)} style={{width: '35px', height: '35px'}}>
                                    <EditIcon />
                                </div>
                            </div>
                        ) : (
                            /* Change Title Input */
                            <Fragment>
                                <input
                                    type="text"
                                    placeholder="Enter a title..."
                                    className="input"
                                    name="title"
                                    maxlength="50"
                                    value={title}
                                    onChange={e => onChange(e)}
                                    style={{width: '100%', boxSizing: 'border-box'}}
                                />

                                {/* Save changes */}
                                <div style={{width: '100%', boxSizing:'border-box', padding:'0 10px', display:'flex', justifyContent:'flex-end'}}>
                                    <button onClick={() => {setEditMode(false); setFormData({ ...formData, title: (adDoc?.title ? adDoc.title : "")})}} className={`secondaryBtn red`}>
                                        Cancel
                                    </button>
                                    <button onClick={handleSaveChanges} disabled={(title === adDoc?.title || title === '')} className={`secondaryBtn  ${(title === adDoc?.title || title === '') ? 'disabled' : 'active'}`}>
                                        Save Changes
                                    </button>
                                </div>
                            </Fragment>
                        )}
                    </div>

                    <div style={{padding: '0 0 12px'}}>
                        <hr style={{height: '6px', width: '100%', background: '#f6f6f6'}} />
                    </div>

                    <div style={{padding: '0 20px'}}>

                        {/* Overview Section */}

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '8px', paddingBottom: '12px', boxSizing: 'border-box'}}>
                            <h2 style={{fontSize: '20px', display: 'flex', alignItems: 'center'}}>
                                Overview
                                <span onClick={() => toggleToolTipModal("overview")} className='icon_20 hover_btn_bg' style={{marginLeft: '5px'}}>
                                    <HelpOutlineOutlinedIcon />
                                </span>
                            </h2>
                        </div>

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                            <span style={{fontSize: '16px'}}>Views</span>
                            <span>{post?.analytics?.views ?? '--'}</span>
                        </div>

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                            <span style={{fontSize: '16px'}}>Post interactions</span>
                            {adDoc ? (
                                <Fragment>
                                    {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                        <span>--</span>
                                    ) : (
                                        <span>
                                            {((post?.likes?.length || 0) + (post?.analytics?.direct_messages || 0) + (post?.analytics?.copied_link || 0) + (post?.analytics?.external_link_clicks || 0)) ?? '--'}
                                        </span>
                                    )}
                                </Fragment>
                            ) : (
                                <span>--</span>
                            )}
                        </div>

                        {ctaType === "profile" && (
                            <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                                <span style={{fontSize: '16px'}}><b>Goal • </b> Profile visits</span>
                                {adDoc ? (
                                    <Fragment>
                                        {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                            <span>--</span>
                                        ) : (
                                            <span>{post?.analytics?.profile_visits ?? '--'}</span>
                                        )}
                                    </Fragment>
                                ) : (
                                    <span>--</span>
                                )}
                            </div>
                        )}

                        {ctaType === "message" && (
                            <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                                <span style={{fontSize: '16px'}}><b>Goal • </b> Direct messages</span>
                                {adDoc ? (
                                    <Fragment>
                                        {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                            <span>--</span>
                                        ) : (
                                            <span>{post?.analytics?.direct_messages ?? '--'}</span>
                                        )}
                                    </Fragment>
                                ) : (
                                    <span>--</span>
                                )}
                            </div>
                        )}

                        {ctaType === "website" && (
                            <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                                <span style={{fontSize: '16px'}}><b>Goal • </b> External link clicks</span>
                                {adDoc ? (
                                    <Fragment>
                                        {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                            <span>--</span>
                                        ) : (
                                            <span>{post?.analytics?.external_link_clicks ?? '--'}</span>
                                        )}
                                    </Fragment>
                                ) : (
                                    <span>--</span>
                                )}
                            </div>
                        )}
                    </div>

                    <div style={{padding: '24px 0 8px'}}>
                        <hr style={{height: '6px', width: '100%', background: '#f6f6f6'}} />
                    </div>

                    {post ? (
                        <Fragment>
                            <Post postDoc={post} history={history} isPreview />
                            {/* Spacer */}
                            <div style={{height: '20px'}}></div>
                        </Fragment>
                    ) : (
                        <Spinner />
                    )}

                    <div style={{padding: '0 20px'}}>
                        {/* Views sections */}

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '8px', paddingBottom: '12px', boxSizing: 'border-box'}}>
                            <h2 style={{fontSize: '20px', display: 'flex', alignItems: 'center'}}>
                                Views
                                <span onClick={() => toggleToolTipModal("views")} className='icon_20 hover_btn_bg' style={{marginLeft: '5px'}}>
                                    <HelpOutlineOutlinedIcon />
                                </span>
                            </h2>
                            {adDoc ? (
                                <Fragment>
                                    {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                        <h2 style={{fontSize: '20px'}}>--</h2>
                                    ) : (
                                        <h2 style={{fontSize: '20px'}}>
                                            {post?.analytics?.views ?? '--'}
                                        </h2>
                                    )}
                                </Fragment>
                            ) : (
                                <h2 style={{fontSize: '20px'}}>--</h2>
                            )}
                        </div>

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                            <span style={{fontSize: '16px'}}>Accounts reached</span>
                            {adDoc ? (
                                <Fragment>
                                    {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                        <span>--</span>
                                    ) : (
                                        <span>{post?.analytics?.accounts_reached ?? '--'}</span>
                                    )}
                                </Fragment>
                            ) : (
                                <span>--</span>
                            )}
                        </div>

                    </div>

                    <div style={{padding: '24px 0'}}>
                        <hr style={{height: '6px', width: '100%', background: '#f6f6f6'}} />
                    </div>

                    <div style={{padding: '0 20px'}}>

                        {/* Interactions Section */}
                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '8px', paddingBottom: '12px', boxSizing: 'border-box'}}>
                            <h2 style={{fontSize: '20px', display: 'flex', alignItems: 'center'}}>
                                Interactions
                                <span onClick={() => toggleToolTipModal("interactions")} className='icon_20 hover_btn_bg' style={{marginLeft: '5px'}}>
                                    <HelpOutlineOutlinedIcon />
                                </span>
                            </h2>
                            {adDoc ? (
                                <Fragment>
                                    {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                        <h2 style={{fontSize: '20px'}}>--</h2>
                                    ) : (
                                        <h2 style={{fontSize: '20px'}}>
                                            {((post?.likes?.length || 0) + (post?.analytics?.direct_messages || 0) + (post?.analytics?.copied_link || 0) + (post?.analytics?.external_link_clicks || 0)) ?? '--'}
                                        </h2>
                                    )}
                                </Fragment>
                            ) : (
                                <h2 style={{fontSize: '20px'}}>--</h2>
                            )}
                        </div>

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                            <span style={{fontSize: '16px'}}>Saves</span>
                            {adDoc ? (
                                <Fragment>
                                    {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                        <span>--</span>
                                    ) : (
                                        <span>{post?.likes?.length ?? '--'}</span>
                                    )}
                                </Fragment>
                            ) : (
                                <span>--</span>
                            )}
                        </div>


                        {/* <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                            <span style={{fontSize: '16px'}}>Comments</span>
                            <span>--</span>
                        </div> */}

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                            <span style={{fontSize: '16px'}}>Direct messages</span>
                            {adDoc ? (
                                <Fragment>
                                    {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                        <span>--</span>
                                    ) : (
                                        <span>{post?.analytics?.direct_messages ?? '--'}</span>
                                    )}
                                </Fragment>
                            ) : (
                                <span>--</span>
                            )}
                        </div>

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                            <span style={{fontSize: '16px'}}>Copied Link</span>
                            {adDoc ? (
                                <Fragment>
                                    {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                        <span>--</span>
                                    ) : (
                                        <span>{post?.analytics?.copied_link ?? '--'}</span>
                                    )}
                                </Fragment>
                            ) : (
                                <span>--</span>
                            )}
                        </div>

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                            <span style={{fontSize: '16px'}}>External Link clicks</span>
                            {adDoc ? (
                                <Fragment>
                                    {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                        <span>--</span>
                                    ) : (
                                        <span>{post?.analytics?.external_link_clicks ?? '--'}</span>
                                    )}
                                </Fragment>
                            ) : (
                                <span>--</span>
                            )}
                        </div>
                    </div>

                    <div style={{padding: '24px 0'}}>
                        <hr style={{height: '6px', width: '100%', background: '#f6f6f6'}} />
                    </div>
                    
                    <div style={{padding: '0 20px'}}>

                        {/* Profile Activity Section */}

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '8px', paddingBottom: '12px', boxSizing: 'border-box'}}>
                            <h2 style={{fontSize: '20px', display: 'flex', alignItems: 'center'}}>
                                Profile Visits
                                <span onClick={() => toggleToolTipModal("profile visits")} className='icon_20 hover_btn_bg' style={{marginLeft: '5px'}}>
                                    <HelpOutlineOutlinedIcon />
                                </span>
                            </h2>
                            {adDoc ? (
                                <Fragment>
                                    {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                        <h2 style={{fontSize: '20px'}}>--</h2>
                                    ) : (
                                        <h2 style={{fontSize: '20px'}}>
                                            {post?.analytics?.profile_visits ?? '--'}
                                        </h2>
                                    )}
                                </Fragment>
                            ) : (
                                <h2 style={{fontSize: '20px'}}>--</h2>
                            )}
                        </div>
                    </div>

                    <div style={{padding: '24px 0'}}>
                        <hr style={{height: '6px', width: '100%', background: '#f6f6f6'}} />
                    </div>

                    <div style={{padding: '0 20px'}}>

                        {/* Details Section */}

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '8px', paddingBottom: '12px', boxSizing: 'border-box'}}>
                            <h2 style={{fontSize: '20px', display: 'flex', alignItems: 'center'}}>
                                Details
                                <span onClick={() => toggleToolTipModal("details")} className='icon_20 hover_btn_bg' style={{marginLeft: '5px'}}>
                                    <HelpOutlineOutlinedIcon />
                                </span>
                            </h2>
                        </div>

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                            <span style={{fontSize: '16px'}}>Status</span>
                            
                            {statusComponent}
                        </div>


                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                            <span style={{fontSize: '16px'}}>Spend</span>
                            {adDoc ? (
                                <Fragment>
                                    {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                        <span>-- of {adBudget}</span>
                                    ) : (
                                        <span>
                                            {adSpend} of {adBudget}
                                        </span>
                                    )}
                                </Fragment>
                            ) : (
                                <span>--</span>
                            )}
                        </div>

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                            <span style={{fontSize: '16px'}}>Runtime</span>
                            {adDoc ? (
                                <Fragment>
                                    {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                        <span>--</span>
                                    ) : (
                                        <span>{formatTimeLeft(adDuration)}</span>
                                    )}
                                </Fragment>
                            ) : (
                                <span>--</span>
                            )}
                        </div>

                        <div style={{width: '100%', display: 'flex', justifyContent: 'space-between', padding: '8px', boxSizing: 'border-box'}}>
                            <span style={{fontSize: '16px'}}>Time left</span>
                            {adDoc ? (
                                <Fragment>
                                    {(adDoc?.status === 'checkout' || adDoc?.status === 'scheduled' || adDoc?.status === 'in review') ? (
                                        <span>--</span>
                                    ) : (
                                        <span>{formatTimeLeft(timeLeft)}</span>
                                    )}
                                </Fragment>
                            ) : (
                                <span>--</span>
                            )}
                        </div>

                        {(adDoc?.status === "active" || adDoc?.status === "paused") && (
                            <Fragment>
                                <div style={{padding: '24px 0'}}>
                                    <hr style={{height: '6px', width: '100%', background: '#f6f6f6'}} />
                                </div>

                                <div style={{width: '100%', display: 'flex'}}>
        
                                    {adDoc?.status == "paused" ? (
                                        <Fragment>
                                            <div style={{flex: 0.5, padding: '10px 5px 0px 0px', boxSizing: 'border-box', zIndex: 10}}>
                                                <Button onClick={toggleStopModal} variant="outlined" className="sidebar__tweet danger noMargin" fullWidth>
                                                    Stop
                                                </Button>
                                            </div>
            
                                            <div style={{flex: 0.5, padding: '10px 5px 0px 0px', boxSizing: 'border-box', zIndex: 10}}>
                                                <Button onClick={toggleResumeModal} variant="outlined" className="sidebar__tweet black noMargin" fullWidth>
                                                    Resume
                                                </Button>
                                            </div>
                                        </Fragment>
                                    ) : (
                                        <div style={{flex: 1, padding: '10px 5px 0px 0px', boxSizing: 'border-box', zIndex: 10}}>
                                            <Button onClick={togglePauseModal} variant="outlined" className="sidebar__tweet black noMargin" fullWidth>
                                                Pause Ad
                                            </Button>
                                        </div>
                                    )}
                            
                                </div>
                            </Fragment>
                        )}
                    </div>

                    <div style={{padding: '24px 0'}}>
                        <hr style={{height: '6px', width: '100%', background: '#f6f6f6'}} />
                    </div>

                    <div onClick={() => window.location = '/settings/help'} className='checkout_form_toggle'>
                        <div style={{minWidth:'100%', minHeight:'48px', alignItems:'center', display:'flex', padding:'8px 0', boxSizing:'border-box'}}>
                            <div style={{marginRight:'12px', height:'40px', alignItems:'center', justifyContent:'center', display:'flex', width:'40px', padding:'6px', borderRadius:'40px'}}>
                                <HeadsetMicOutlinedIcon />
                            </div>
                            <div style={{flexGrow: 1}}>
                                <h2 style={{fontWeight:'bold', lineHeight:'23.8px', fontSize:'18px'}}>
                                    Customer Support
                                </h2>
                                <p style={{margin: '6px 0', margin: '0', fontSize: '14px'}}>
                                    Have an issue? Chat with us.
                                </p>
                            </div>
                            <div style={{display:'flex', alignItems:'center'}}>
                                <div style={{fontSize:'32px', marginLeft:'32px'}}>
                                    <ChevronRightIcon />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{height: '100px'}}></div>

                </div>

            </div>

            {/* Stop Ad Modal */}
            <ModalContainer show={stopModal} onClose={toggleStopModal}>
                <Modal>

                    {/* Modal Title */}
                    <div>
                        <h2>Stop Ad?</h2>
                    </div>

                    {/* Modal Description */}
                    <p>This can’t be undone and it will be removed from your profile, the main feed, and from any search results.</p>
                    
                    {/* Modal Actions */}
                    <Button 
                        onClick={handleStop}
                        variant="outlined" 
                        className="sidebar__tweet danger"
                        fullWidth
                    >
                        Stop
                    </Button>
                    <Button 
                        onClick={toggleStopModal}
                        variant="outlined" 
                        className="sidebar__tweet ghost blackText"
                        fullWidth
                    >
                        Cancel
                    </Button>
                </Modal>
            </ModalContainer>

            {/* Pause Ad Modal */}
            <ModalContainer show={pauseModal} onClose={togglePauseModal}>
                <Modal>

                    {/* Modal Title */}
                    <div>
                        <h2>Pause Ad?</h2>
                    </div>

                    {/* Modal Description */}
                    <p>This can’t be undone and it will be removed from your profile, the main feed, and from any search results.</p>
                    
                    {/* Modal Actions */}
                    <Button 
                        onClick={handlePause}
                        variant="outlined" 
                        className="sidebar__tweet black"
                        fullWidth
                    >
                        Pause
                    </Button>
                    <Button 
                        onClick={togglePauseModal}
                        variant="outlined" 
                        className="sidebar__tweet ghost blackText"
                        fullWidth
                    >
                        Cancel
                    </Button>
                </Modal>
            </ModalContainer>

            {/* Resume Ad Modal */}
            <ModalContainer show={resumeModal} onClose={toggleResumeModal}>
                <Modal>

                    {/* Modal Title */}
                    <div>
                        <h2>Resume Ad?</h2>
                    </div>

                    {/* Modal Description */}
                    <p>This can’t be undone and it will be removed from your profile, the main feed, and from any search results.</p>
                    
                    {/* Modal Actions */}
                    <Button 
                        onClick={handleResume}
                        variant="outlined" 
                        className="sidebar__tweet black"
                        fullWidth
                    >
                        Resume
                    </Button>
                    <Button 
                        onClick={toggleResumeModal}
                        variant="outlined" 
                        className="sidebar__tweet ghost blackText"
                        fullWidth
                    >
                        Cancel
                    </Button>
                </Modal>
            </ModalContainer>

            {/* Tooltip Modals */}
            <ModalContainer show={toolTipModal} onClose={toggleToolTipModal}>
                <Modal>

                    {toolTipType === 'overview' && (
                        <Fragment>
                            {/* Title + Description */}
                            <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Views</h3>
                            <p>The # of times your post was displayed.</p>
                            
                            <div style={{height: '15px'}}></div>

                            {/* Title + Description */}
                            <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Post Interactions</h3>
                            <p>The # of saves, link clicks, DM's, and accounts that shared your post.</p>

                            <div style={{height: '15px'}}></div>
                            
                            {ctaType === "profile" && (
                                <Fragment>
                                    {/* Title + Description */}
                                    <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Profile visits</h3>
                                    <p>The # of times your profile was visited.</p>
                                </Fragment>
                            )}

                            {ctaType === "message" && (
                                <Fragment>
                                    {/* Title + Description */}
                                    <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Direct messages</h3>
                                    <p>The # of direct messages you received from this post.</p>
                                </Fragment>
                            )}

                            {ctaType === "website" && (
                                <Fragment>
                                    {/* Title + Description */}
                                    <h3 style={{fontWeight: 500, marginBottom: '5px'}}>External link clicks</h3>
                                    <p>The # of taps on any of the external links on your post.</p>
                                </Fragment>
                            )}
                        </Fragment>
                    )}

                    {toolTipType === 'views' && (
                        <Fragment>
                            {/* Title + Description */}
                            <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Views</h3>
                            <p>The # of times your post was displayed.</p>
                            
                            <div style={{height: '15px'}}></div>

                            {/* Title + Description */}
                            <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Accounts reached</h3>
                            <p>The # of unique accounts that have seen this ad. This is different from views, which may include multiple impressions of your ad by the same account.</p>
                        </Fragment>
                    )}

                    {toolTipType === 'interactions' && (
                        <Fragment>
                            {/* Title + Description */}
                            <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Saves</h3>
                            <p>The # of saves on your post minus the number of unsaves.</p>
                            
                            <div style={{height: '15px'}}></div>

                            {/* Title + Description */}
                            {/* <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Comments</h3>
                            <p>The # of comments on your post minus the number of deleted comments.</p>

                            <div style={{height: '15px'}}></div> */}

                            {/* Title + Description */}
                            <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Direct messages</h3>
                            <p>The # of direct messages you received from this post.</p>
                            
                            <div style={{height: '15px'}}></div>

                            {/* Title + Description */}
                            <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Copied link</h3>
                            <p>The # of times the link to your post was copied</p>
                            
                            <div style={{height: '15px'}}></div>

                            {/* Title + Description */}
                            <h3 style={{fontWeight: 500, marginBottom: '5px'}}>External link clicks</h3>
                            <p>The # of taps on any of the external links on your post.</p>

                            <div style={{height: '15px'}}></div>
                            
                        </Fragment>
                    )}

                    {toolTipType === 'profile visits' && (
                        <Fragment>
                            {/* Title + Description */}
                            <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Profile visits</h3>
                            <p>The # of times your profile was visited.</p>
                            
                        </Fragment>
                    )}

                    {toolTipType === 'details' && (
                        <Fragment>
                            {/* Title + Description */}
                            <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Status</h3>
                            <p>The current status of your ad.</p>

                            <div style={{height: '15px'}}></div>

                            {/* Title + Description */}
                            <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Spend</h3>
                            <p>The amount of your ad budget that has been spent.</p>

                            <div style={{height: '15px'}}></div>

                            {/* Title + Description */}
                            <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Runtime</h3>
                            <p>The length of time your ad has been running.</p>

                            <div style={{height: '15px'}}></div>

                            {/* Title + Description */}
                            <h3 style={{fontWeight: 500, marginBottom: '5px'}}>Time left</h3>
                            <p>The amount of time your ad has left to run before it expires.</p>
                            
                        </Fragment>
                    )}

                    <div style={{padding: '12px 0 4px'}}>
                        <hr style={{height: '1px', width: '100%', background: '#e6ecf0'}} />
                    </div>

                    <p style={{fontSize: '14px'}}>-- shows when data is currently unavailable.</p>
                </Modal>
            </ModalContainer>
        </Fragment>
    )
}

Ad_Details.propTypes = {
    ad: PropTypes.object.isRequired,
    post: PropTypes.object.isRequired,
    setAlert: PropTypes.func.isRequired,
    pauseAd: PropTypes.func.isRequired,
    activateAd: PropTypes.func.isRequired,
    stopAd: PropTypes.func.isRequired,
    resumeAd: PropTypes.func.isRequired,
    editAdTitle: PropTypes.func.isRequired,
    getPostById: PropTypes.func.isRequired,
}

const mapStateToProps = state => ({

    // Mapping the states from the Redux store to the below props
    ad: state.ad,
    post: state.post
});

export default connect(mapStateToProps, { 

    // Connecting actions from redux to the component
    setAlert,
    pauseAd, 
    activateAd, 
    stopAd,
    resumeAd,
    editAdTitle,
    getPostById
})(Ad_Details);